import React, { useEffect, useMemo, useState } from 'react';
import { getOrders, deleteOrder, getMasterData } from '../api';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MasterDataAutocomplete } from '../components/MasterDataAutocomplete';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { formatDate, getStatusColor, isCancelledOrder } from '../lib/utils';
import { Search, Plus, Trash2, MoreVertical, Info, Download, X, ArrowUp, ArrowDown, ArrowUpDown, Filter, Columns3 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useAuth } from '../AuthContext';
import { useScreenState, useScrollRestoration } from '../hooks/useScreenState';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import ExportDialog from '../components/ExportDialog';
import useExportShortcut from '../hooks/useExportShortcut';
import { exportToExcel, exportToPDF, EXPORT_COLUMNS } from '../utils/exportUtils';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [query]);
  return matches;
}

const COLUMNS_STORAGE_KEY = 'ordersTableVisibleColumns';

const COLUMN_GROUPS = ['Order', 'Parties / Commercial', 'Quantity / Fulfilment', 'Goods', 'Dispatch'];

const COLUMN_DEFINITIONS = [
  // ORDER
  { key: 'indent_number', label: 'Indent No.', group: 'Order', sortable: true, defaultVisible: true },
  { key: 'status', label: 'Status', group: 'Order', sortable: true, defaultVisible: true },
  { key: 'date', label: 'Date', group: 'Order', sortable: true, defaultVisible: true },
  { key: 'purchase_indent_number', label: 'Purchase Indent No.', group: 'Order', sortable: true, defaultVisible: false },
  // PARTIES / COMMERCIAL
  { key: 'buyer', label: 'Buyer', group: 'Parties / Commercial', sortable: true, defaultVisible: true, align: 'left' },
  { key: 'supplier', label: 'Supplier', group: 'Parties / Commercial', sortable: true, defaultVisible: true, align: 'left' },
  { key: 'purchase_rate', label: 'Purchase Rate', group: 'Parties / Commercial', sortable: false, defaultVisible: false, align: 'right' },
  { key: 'purchase_cd_percent', label: 'P.CD %', group: 'Parties / Commercial', sortable: false, defaultVisible: false, align: 'right' },
  { key: 'sales_rate', label: 'Sale Rate', group: 'Parties / Commercial', sortable: false, defaultVisible: false, align: 'right' },
  { key: 'sales_cd_percent', label: 'S.CD %', group: 'Parties / Commercial', sortable: false, defaultVisible: false, align: 'right' },
  // QUANTITY / FULFILMENT
  { key: 'total_qty', label: 'Total Quantity', group: 'Quantity / Fulfilment', sortable: true, defaultVisible: true, align: 'right' },
  { key: 'delivered_qty', label: 'Delivered Quantity', group: 'Quantity / Fulfilment', sortable: true, defaultVisible: false, align: 'right' },
  { key: 'remaining', label: 'Remaining Quantity', group: 'Quantity / Fulfilment', sortable: true, defaultVisible: true, align: 'right' },
  { key: 'cancelled_qty', label: 'Cancelled Quantity', group: 'Quantity / Fulfilment', sortable: true, defaultVisible: false, align: 'right' },
  { key: 'deliveries', label: 'Deliveries', group: 'Quantity / Fulfilment', sortable: true, defaultVisible: true, align: 'right' },
  // GOODS
  { key: 'trade_name', label: 'Trade Name', group: 'Goods', sortable: false, defaultVisible: false },
  { key: 'fabric_type', label: 'Fabric Type', group: 'Goods', sortable: false, defaultVisible: false },
  { key: 'design_color_number', label: 'Design / Color No.', group: 'Goods', sortable: false, defaultVisible: false },
  // DISPATCH
  { key: 'transporter_name', label: 'Transport Name', group: 'Dispatch', sortable: false, defaultVisible: false },
  { key: 'lr_number', label: 'LR No.', group: 'Dispatch', sortable: false, defaultVisible: false },
  { key: 'bale_number', label: 'Bale No.', group: 'Dispatch', sortable: false, defaultVisible: false },
  { key: 'delivery_date', label: 'Delivery Date', group: 'Dispatch', sortable: false, defaultVisible: false },
];

const DEFAULT_VISIBLE_COLUMNS = COLUMN_DEFINITIONS.filter((c) => c.defaultVisible).map((c) => c.key);

const COLUMN_WIDTHS_STORAGE_KEY = 'ordersTableColumnWidths';
const COLUMN_ORDER_STORAGE_KEY = 'ordersTableColumnOrder';
const CHECKBOX_COLUMN_WEIGHT = 40;
const MIN_COLUMN_WEIGHT = 50;

const DEFAULT_COLUMN_WIDTHS = {
  indent_number: 110,
  status: 130,
  date: 110,
  purchase_indent_number: 150,
  buyer: 180,
  supplier: 180,
  purchase_rate: 100,
  purchase_cd_percent: 80,
  sales_rate: 100,
  sales_cd_percent: 80,
  total_qty: 110,
  delivered_qty: 130,
  remaining: 140,
  cancelled_qty: 130,
  deliveries: 100,
  trade_name: 150,
  fabric_type: 100,
  design_color_number: 130,
  transporter_name: 140,
  lr_number: 100,
  bale_number: 100,
  delivery_date: 120,
};

// Flatten printed + dyed fabrics into one line-item list per order (one line per printed fabric / dyed fixed-chart row / dyed pick&choose color)
const getFabricLines = (order) => {
  const lines = [];
  (order.printed_fabrics || []).forEach((pf) => {
    lines.push({
      trade_name: pf.trade_name || '-',
      fabric_type: 'Printed',
      design_number: pf.design_number || '-',
      color_number: '-',
      purchase_rate: pf.purchase_rate || 0,
      purchase_cd_percent: pf.purchase_cd_percent || 0,
      sales_rate: pf.sales_rate || 0,
      sales_cd_percent: pf.sales_cd_percent || 0,
    });
  });
  (order.dyed_fabrics || []).forEach((df) => {
    if (df.mode === 'pick_choose' && df.colors && df.colors.length > 0) {
      df.colors.forEach((c) => {
        lines.push({
          trade_name: df.trade_name || '-',
          fabric_type: 'Dyed',
          design_number: '-',
          color_number: c.color_number != null ? String(c.color_number) : '-',
          purchase_rate: c.purchase_rate || 0,
          purchase_cd_percent: df.trade_purchase_cd_percent || 0,
          sales_rate: c.sales_rate || 0,
          sales_cd_percent: df.trade_sales_cd_percent || 0,
        });
      });
    } else {
      lines.push({
        trade_name: df.trade_name || '-',
        fabric_type: 'Dyed',
        design_number: df.design_number || '-',
        color_number: '-',
        purchase_rate: df.purchase_rate || 0,
        purchase_cd_percent: df.purchase_cd_percent || 0,
        sales_rate: df.sales_rate || 0,
        sales_cd_percent: df.sales_cd_percent || 0,
      });
    }
  });
  return lines;
};

// Flatten deliveries -> bales into one dispatch line-item list per order (one line per bale)
const getDispatchLines = (order) => {
  const lines = [];
  (order.deliveries || []).forEach((d) => {
    if (d.bales && d.bales.length > 0) {
      d.bales.forEach((b) => {
        lines.push({
          transporter_name: d.transporter_name || '-',
          delivery_date: d.delivery_date || '',
          lr_number: b.lr_number || '-',
          bale_number: b.bale_number || '-',
        });
      });
    } else {
      lines.push({
        transporter_name: d.transporter_name || '-',
        delivery_date: d.delivery_date || '',
        lr_number: '-',
        bale_number: '-',
      });
    }
  });
  return lines;
};

const getDeliveredQty = (order) =>
  (order.deliveries || []).filter((d) => d.status !== 'Cancelled').reduce((sum, d) => sum + (d.total_delivered_quantity || 0), 0);

const getCancelledQty = (order) =>
  (order.deliveries || []).filter((d) => d.status === 'Cancelled').reduce((sum, d) => sum + (d.total_delivered_quantity || 0), 0);

const matchesTextFilter = (values, filterText) => {
  if (!filterText) return true;
  const joined = (values || []).join(' ').toLowerCase();
  return joined.includes(filterText.toLowerCase());
};

export default function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useScreenState('orders.filters', {
    status: '',
    buyer: '',
    supplier: '',
    trade_name: '',
    indent_number: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedOrderInfo, setSelectedOrderInfo] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Master data for filter dropdowns
  const [masterBuyers, setMasterBuyers] = useState([]);
  const [masterSuppliers, setMasterSuppliers] = useState([]);
  const [masterTradeNames, setMasterTradeNames] = useState([]);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDesktopView = useMediaQuery('(min-width: 1024px)');
  const [sortConfig, setSortConfig] = useScreenState('orders.sortConfig', { key: null, direction: 'asc' });
  const [columnFilters, setColumnFilters] = useScreenState('orders.columnFilters', {
    indent_number: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    buyer: '',
    supplier: '',
    purchase_indent_number: '',
    purchase_rate: '',
    purchase_cd_percent: '',
    sales_rate: '',
    sales_cd_percent: '',
    delivered_qty: '',
    cancelled_qty: '',
    trade_name: '',
    fabric_type: '',
    design_color_number: '',
    transporter_name: '',
    lr_number: '',
    bale_number: '',
    deliveryDateFrom: '',
    deliveryDateTo: '',
  });
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) { /* ignore corrupt storage */ }
    return DEFAULT_VISIBLE_COLUMNS;
  });

  useEffect(() => {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };
  const resetColumns = () => setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  const isColumnVisible = (key) => visibleColumns.includes(key);

  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') return { ...DEFAULT_COLUMN_WIDTHS, ...parsed };
      }
    } catch (e) { /* ignore corrupt storage */ }
    return { ...DEFAULT_COLUMN_WIDTHS };
  });

  useEffect(() => {
    localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(columnWidths));
  }, [columnWidths]);

  const resetColumnWidths = () => setColumnWidths({ ...DEFAULT_COLUMN_WIDTHS });

  const ALL_COLUMN_KEYS = COLUMN_DEFINITIONS.map((c) => c.key);

  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Keep saved order, append any new columns (schema changes) at the end, drop unknown/stale keys
          const known = parsed.filter((k) => ALL_COLUMN_KEYS.includes(k));
          const missing = ALL_COLUMN_KEYS.filter((k) => !known.includes(k));
          return [...known, ...missing];
        }
      }
    } catch (e) { /* ignore corrupt storage */ }
    return ALL_COLUMN_KEYS;
  });

  useEffect(() => {
    localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(columnOrder));
  }, [columnOrder]);

  const resetColumnOrder = () => setColumnOrder(ALL_COLUMN_KEYS);

  const getVisibleColumnsOrdered = () =>
    columnOrder
      .filter((key) => isColumnVisible(key))
      .map((key) => COLUMN_DEFINITIONS.find((c) => c.key === key))
      .filter(Boolean);

  const [draggedColKey, setDraggedColKey] = useState(null);
  const [dragOverColKey, setDragOverColKey] = useState(null);

  const handleColumnDragStart = (e, key) => {
    setDraggedColKey(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  };
  const handleColumnDragOver = (e, key) => {
    e.preventDefault();
    if (key !== draggedColKey) setDragOverColKey(key);
  };
  const handleColumnDragLeave = () => setDragOverColKey(null);
  const handleColumnDrop = (e, targetKey) => {
    e.preventDefault();
    setDragOverColKey(null);
    if (!draggedColKey || draggedColKey === targetKey) return;
    setColumnOrder((prev) => {
      const next = prev.filter((k) => k !== draggedColKey);
      const targetIdx = next.indexOf(targetKey);
      next.splice(targetIdx, 0, draggedColKey);
      return next;
    });
  };
  const handleColumnDragEnd = () => {
    setDraggedColKey(null);
    setDragOverColKey(null);
  };


  const getTotalColumnWeight = (visibleCols) => {
    const base = visibleCols.reduce((sum, c) => sum + (columnWidths[c.key] || DEFAULT_COLUMN_WIDTHS[c.key] || 100), 0);
    return base + (selectionMode ? CHECKBOX_COLUMN_WEIGHT : 0);
  };

  const startColumnResize = (e, key, nextKey, totalWeight) => {
    e.preventDefault();
    e.stopPropagation();
    const table = e.currentTarget.closest('table');
    const tableWidthPx = table.getBoundingClientRect().width;
    const pxPerWeight = tableWidthPx / totalWeight;
    const startX = e.clientX;
    const startWeight = columnWidths[key] || DEFAULT_COLUMN_WIDTHS[key] || 100;
    const startNextWeight = columnWidths[nextKey] || DEFAULT_COLUMN_WIDTHS[nextKey] || 100;

    const onMove = (ev) => {
      const deltaPx = ev.clientX - startX;
      let deltaWeight = deltaPx / pxPerWeight;
      let newWeight = startWeight + deltaWeight;
      let newNextWeight = startNextWeight - deltaWeight;
      if (newWeight < MIN_COLUMN_WEIGHT) {
        newWeight = MIN_COLUMN_WEIGHT;
        newNextWeight = startWeight + startNextWeight - MIN_COLUMN_WEIGHT;
      }
      if (newNextWeight < MIN_COLUMN_WEIGHT) {
        newNextWeight = MIN_COLUMN_WEIGHT;
        newWeight = startWeight + startNextWeight - MIN_COLUMN_WEIGHT;
      }
      setColumnWidths((prev) => ({ ...prev, [key]: newWeight, [nextKey]: newNextWeight }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };


  const setColumnFilter = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const statusOptions = useMemo(() => {
    const unique = new Set(orders.map((o) => o.order_details.status).filter(Boolean));
    return Array.from(unique).sort();
  }, [orders]);

  const columnFilteredOrders = useMemo(() => {
    const {
      indent_number, status, dateFrom, dateTo, buyer, supplier,
      purchase_indent_number, purchase_rate, purchase_cd_percent, sales_rate, sales_cd_percent,
      delivered_qty, cancelled_qty, trade_name, fabric_type, design_color_number,
      transporter_name, lr_number, bale_number, deliveryDateFrom, deliveryDateTo,
    } = columnFilters;
    return orders.filter((order) => {
      if (indent_number && !order.order_details.indent_number.toLowerCase().includes(indent_number.toLowerCase())) {
        return false;
      }
      if (status && order.order_details.status !== status) {
        return false;
      }
      if (dateFrom && order.order_details.order_date < dateFrom) {
        return false;
      }
      if (dateTo && order.order_details.order_date > dateTo) {
        return false;
      }
      if (buyer) {
        const buyerStr = order.buyers.map((b) => b.buyer_name).join(', ').toLowerCase();
        if (!buyerStr.includes(buyer.toLowerCase())) return false;
      }
      if (supplier) {
        const supplierStr = order.suppliers.map((s) => s.supplier_name).join(', ').toLowerCase();
        if (!supplierStr.includes(supplier.toLowerCase())) return false;
      }
      if (purchase_indent_number && !matchesTextFilter(order.suppliers.map((s) => s.purchase_indent_number || ''), purchase_indent_number)) {
        return false;
      }
      if (delivered_qty && !matchesTextFilter([getDeliveredQty(order).toFixed(2)], delivered_qty)) {
        return false;
      }
      if (cancelled_qty && !matchesTextFilter([getCancelledQty(order).toFixed(2)], cancelled_qty)) {
        return false;
      }
      const needsFabricLines = purchase_rate || purchase_cd_percent || sales_rate || sales_cd_percent || trade_name || fabric_type || design_color_number;
      if (needsFabricLines) {
        const fabricLines = getFabricLines(order);
        if (purchase_rate && !matchesTextFilter(fabricLines.map((f) => String(f.purchase_rate)), purchase_rate)) return false;
        if (purchase_cd_percent && !matchesTextFilter(fabricLines.map((f) => String(f.purchase_cd_percent)), purchase_cd_percent)) return false;
        if (sales_rate && !matchesTextFilter(fabricLines.map((f) => String(f.sales_rate)), sales_rate)) return false;
        if (sales_cd_percent && !matchesTextFilter(fabricLines.map((f) => String(f.sales_cd_percent)), sales_cd_percent)) return false;
        if (trade_name && !matchesTextFilter(fabricLines.map((f) => f.trade_name), trade_name)) return false;
        if (fabric_type && !fabricLines.some((f) => f.fabric_type === fabric_type)) return false;
        if (design_color_number && !matchesTextFilter(fabricLines.flatMap((f) => [f.design_number, f.color_number]), design_color_number)) return false;
      }
      const needsDispatchLines = transporter_name || lr_number || bale_number || deliveryDateFrom || deliveryDateTo;
      if (needsDispatchLines) {
        const dispatchLines = getDispatchLines(order);
        if (transporter_name && !matchesTextFilter(dispatchLines.map((d) => d.transporter_name), transporter_name)) return false;
        if (lr_number && !matchesTextFilter(dispatchLines.map((d) => d.lr_number), lr_number)) return false;
        if (bale_number && !matchesTextFilter(dispatchLines.map((d) => d.bale_number), bale_number)) return false;
        if (deliveryDateFrom && !dispatchLines.some((d) => d.delivery_date && d.delivery_date >= deliveryDateFrom)) return false;
        if (deliveryDateTo && !dispatchLines.some((d) => d.delivery_date && d.delivery_date <= deliveryDateTo)) return false;
      }
      return true;
    });
  }, [orders, columnFilters]);

  const getSortValue = (order, key) => {
    switch (key) {
      case 'indent_number': return order.order_details.indent_number || '';
      case 'status': return order.order_details.status || '';
      case 'date': return order.order_details.order_date || '';
      case 'buyer': return order.buyers.map((b) => b.buyer_name).join(', ');
      case 'supplier': return order.suppliers.map((s) => s.supplier_name).join(', ');
      case 'purchase_indent_number': return order.suppliers.map((s) => s.purchase_indent_number || '').join(', ');
      case 'total_qty': return order.total_quantity || 0;
      case 'delivered_qty': return getDeliveredQty(order);
      case 'remaining': return order.remaining_balance || 0;
      case 'cancelled_qty': return getCancelledQty(order);
      case 'deliveries': return order.deliveries.length || 0;
      default: return '';
    }
  };

  const sortedOrders = useMemo(() => {
    if (!sortConfig.key) return columnFilteredOrders;
    return [...columnFilteredOrders].sort((a, b) => {
      const valA = getSortValue(a, sortConfig.key);
      const valB = getSortValue(b, sortConfig.key);
      let cmp;
      if (typeof valA === 'number' && typeof valB === 'number') {
        cmp = valA - valB;
      } else {
        cmp = String(valA).localeCompare(String(valB), undefined, { numeric: true, sensitivity: 'base' });
      }
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [columnFilteredOrders, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    ));
  };

  const renderSortableHeader = (label, key, align = 'left', extraClassName = '', filterConfig = null, widthPercent = null, resizeHandle = null, dragProps = {}) => {
    const isActive = sortConfig.key === key;
    return (
      <th
        style={widthPercent != null ? { width: `${widthPercent}%` } : undefined}
        {...dragProps}
        className={`relative px-2 py-3 font-semibold text-maroon-700 whitespace-nowrap select-none overflow-hidden transition-colors duration-100 ${
          align === 'right' ? 'text-right' : 'text-left'
        } ${extraClassName}`}
      >
        <div className={`flex items-center gap-1.5 ${align === 'right' ? 'justify-end' : 'justify-start'}`}>
          <span
            tabIndex={0}
            role="button"
            aria-label={`Sort by ${label}`}
            onClick={() => handleSort(key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSort(key);
              }
            }}
            className="inline-flex items-center gap-1 cursor-pointer outline-none hover:text-maroon-900 focus-visible:ring-2 focus-visible:ring-maroon-600 rounded transition-colors duration-150 truncate"
          >
            <span className="truncate">{label}</span>
            {isActive ? (
              sortConfig.direction === 'asc' ? (
                <ArrowUp className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <ArrowDown className="h-3.5 w-3.5 shrink-0" />
              )
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 text-maroon-300 shrink-0" />
            )}
          </span>
          {filterConfig && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid={`col-filter-trigger-${key}`}
                  aria-label={`Filter ${label}`}
                  draggable={false}
                  onClick={(e) => e.stopPropagation()}
                  className={`p-1 rounded outline-none focus-visible:ring-2 focus-visible:ring-maroon-600 transition-colors duration-150 shrink-0 ${
                    filterConfig.isActive ? 'text-maroon-900 bg-maroon-100' : 'text-maroon-400 hover:bg-maroon-100 hover:text-maroon-700'
                  }`}
                >
                  <Filter className="h-3.5 w-3.5" fill={filterConfig.isActive ? 'currentColor' : 'none'} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align={align === 'right' ? 'end' : 'start'}
                className="w-64 p-3 bg-white"
                onClick={(e) => e.stopPropagation()}
              >
                {filterConfig.content}
              </PopoverContent>
            </Popover>
          )}
        </div>
        {resizeHandle}
      </th>
    );
  };

  // ALT+E shortcut for export
  useExportShortcut(() => setExportDialogOpen(true));

  const buildTextFilterConfig = (key, label, placeholder = 'Type to filter...') => ({
    isActive: !!columnFilters[key],
    content: (
      <div className="space-y-2">
        <p className="text-xs font-semibold text-maroon-700">Filter {label}</p>
        <Input
          type="text"
          autoFocus
          data-testid={`col-filter-${key}`}
          value={columnFilters[key] || ''}
          onChange={(e) => setColumnFilter(key, e.target.value)}
          placeholder={placeholder}
          className="h-8 text-xs"
        />
        {columnFilters[key] && (
          <button
            type="button"
            data-testid={`col-filter-${key}-clear`}
            onClick={() => setColumnFilter(key, '')}
            className="text-xs text-maroon-600 hover:text-maroon-900 underline"
          >
            Clear filter
          </button>
        )}
      </div>
    ),
  });

  const buildSelectFilterConfig = (key, label, options) => ({
    isActive: !!columnFilters[key],
    content: (
      <div className="space-y-2">
        <p className="text-xs font-semibold text-maroon-700">Filter {label}</p>
        <select
          data-testid={`col-filter-${key}`}
          value={columnFilters[key] || ''}
          onChange={(e) => setColumnFilter(key, e.target.value)}
          className="w-full text-xs rounded border border-maroon-200 px-2 py-1.5 outline-none focus:ring-1 focus:ring-maroon-500 bg-white"
        >
          <option value="">All</option>
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        {columnFilters[key] && (
          <button
            type="button"
            data-testid={`col-filter-${key}-clear`}
            onClick={() => setColumnFilter(key, '')}
            className="text-xs text-maroon-600 hover:text-maroon-900 underline"
          >
            Clear filter
          </button>
        )}
      </div>
    ),
  });

  const buildDateRangeFilterConfig = (fromKey, toKey, label) => ({
    isActive: !!(columnFilters[fromKey] || columnFilters[toKey]),
    content: (
      <div className="space-y-2">
        <p className="text-xs font-semibold text-maroon-700">Filter {label}</p>
        <div className="space-y-1.5">
          <label className="block">
            <span className="text-[11px] text-maroon-500">From</span>
            <input
              type="date"
              data-testid={`col-filter-${fromKey}`}
              value={columnFilters[fromKey] || ''}
              onChange={(e) => setColumnFilter(fromKey, e.target.value)}
              className="w-full text-xs rounded border border-maroon-200 px-2 py-1.5 outline-none focus:ring-1 focus:ring-maroon-500 bg-white"
            />
          </label>
          <label className="block">
            <span className="text-[11px] text-maroon-500">To</span>
            <input
              type="date"
              data-testid={`col-filter-${toKey}`}
              value={columnFilters[toKey] || ''}
              onChange={(e) => setColumnFilter(toKey, e.target.value)}
              className="w-full text-xs rounded border border-maroon-200 px-2 py-1.5 outline-none focus:ring-1 focus:ring-maroon-500 bg-white"
            />
          </label>
        </div>
        {(columnFilters[fromKey] || columnFilters[toKey]) && (
          <button
            type="button"
            data-testid={`col-filter-${fromKey}-clear`}
            onClick={() => { setColumnFilter(fromKey, ''); setColumnFilter(toKey, ''); }}
            className="text-xs text-maroon-600 hover:text-maroon-900 underline"
          >
            Clear filter
          </button>
        )}
      </div>
    ),
  });

  const getFilterConfigForColumn = (col) => {
    switch (col.key) {
      case 'indent_number': return buildTextFilterConfig('indent_number', 'Indent No.');
      case 'status': return buildSelectFilterConfig('status', 'Status', statusOptions);
      case 'date': return buildDateRangeFilterConfig('dateFrom', 'dateTo', 'Date Range');
      case 'buyer': return buildTextFilterConfig('buyer', 'Buyer');
      case 'supplier': return buildTextFilterConfig('supplier', 'Supplier');
      case 'purchase_indent_number': return buildTextFilterConfig('purchase_indent_number', 'Purchase Indent No.');
      case 'purchase_rate': return buildTextFilterConfig('purchase_rate', 'Purchase Rate');
      case 'purchase_cd_percent': return buildTextFilterConfig('purchase_cd_percent', 'P.CD %');
      case 'sales_rate': return buildTextFilterConfig('sales_rate', 'Sale Rate');
      case 'sales_cd_percent': return buildTextFilterConfig('sales_cd_percent', 'S.CD %');
      case 'delivered_qty': return buildTextFilterConfig('delivered_qty', 'Delivered Quantity');
      case 'cancelled_qty': return buildTextFilterConfig('cancelled_qty', 'Cancelled Quantity');
      case 'trade_name': return buildTextFilterConfig('trade_name', 'Trade Name');
      case 'fabric_type': return buildSelectFilterConfig('fabric_type', 'Fabric Type', ['Printed', 'Dyed']);
      case 'design_color_number': return buildTextFilterConfig('design_color_number', 'Design / Color No.');
      case 'transporter_name': return buildTextFilterConfig('transporter_name', 'Transport Name');
      case 'lr_number': return buildTextFilterConfig('lr_number', 'LR No.');
      case 'bale_number': return buildTextFilterConfig('bale_number', 'Bale No.');
      case 'delivery_date': return buildDateRangeFilterConfig('deliveryDateFrom', 'deliveryDateTo', 'Delivery Date');
      default: return null;
    }
  };

  const renderColumnHeader = (col, idx, visibleCols, totalWeight) => {
    const filterConfig = getFilterConfigForColumn(col);
    const weight = columnWidths[col.key] || DEFAULT_COLUMN_WIDTHS[col.key] || 100;
    const widthPercent = (weight / totalWeight) * 100;
    const nextCol = visibleCols[idx + 1];
    const resizeHandle = nextCol ? (
      <div
        draggable={false}
        onMouseDown={(e) => startColumnResize(e, col.key, nextCol.key, totalWeight)}
        data-testid={`resize-handle-${col.key}`}
        title="Drag to resize column"
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-maroon-400/60 active:bg-maroon-500/70 z-20"
      />
    ) : null;
    const isDragging = draggedColKey === col.key;
    const isDragOver = dragOverColKey === col.key && draggedColKey !== col.key;
    const dragClassName = `cursor-grab ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'bg-maroon-200/70 ring-2 ring-inset ring-maroon-500' : ''}`;
    const dragProps = {
      draggable: true,
      onDragStart: (e) => handleColumnDragStart(e, col.key),
      onDragOver: (e) => handleColumnDragOver(e, col.key),
      onDragLeave: handleColumnDragLeave,
      onDrop: (e) => handleColumnDrop(e, col.key),
      onDragEnd: handleColumnDragEnd,
    };
    if (col.sortable) {
      return (
        <React.Fragment key={col.key}>
          {renderSortableHeader(col.label, col.key, col.align || 'left', `${col.extraClassName || ''} ${dragClassName}`, filterConfig, widthPercent, resizeHandle, dragProps)}
        </React.Fragment>
      );
    }
    return (
      <th
        key={col.key}
        style={{ width: `${widthPercent}%` }}
        {...dragProps}
        className={`relative px-2 py-3 font-semibold text-maroon-700 whitespace-nowrap select-none overflow-hidden transition-colors duration-100 ${
          col.align === 'right' ? 'text-right' : 'text-left'
        } ${col.extraClassName || ''} ${dragClassName}`}
      >
        <div className={`flex items-center gap-1.5 ${col.align === 'right' ? 'justify-end' : 'justify-start'}`}>
          <span className="truncate">{col.label}</span>
          {filterConfig && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid={`col-filter-trigger-${col.key}`}
                  aria-label={`Filter ${col.label}`}
                  draggable={false}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={`p-1 rounded outline-none focus-visible:ring-2 focus-visible:ring-maroon-600 transition-colors duration-150 shrink-0 ${
                    filterConfig.isActive ? 'text-maroon-900 bg-maroon-100' : 'text-maroon-400 hover:bg-maroon-100 hover:text-maroon-700'
                  }`}
                >
                  <Filter className="h-3.5 w-3.5" fill={filterConfig.isActive ? 'currentColor' : 'none'} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align={col.align === 'right' ? 'end' : 'start'}
                className="w-64 p-3 bg-white"
                onClick={(e) => e.stopPropagation()}
              >
                {filterConfig.content}
              </PopoverContent>
            </Popover>
          )}
        </div>
        {resizeHandle}
      </th>
    );
  };

  const renderMultiValueCell = (values) => {
    if (!values || values.length === 0) {
      return <span className="text-gray-400">-</span>;
    }
    return values.map((v, idx) => <div key={idx} className="leading-snug break-words">{v}</div>);
  };

  const renderCell = (col, order, fabricLines, dispatchLines, isCancelled) => {
    const textColor = isCancelled ? 'text-red-800' : 'text-gray-900';
    switch (col.key) {
      case 'indent_number':
        return (
          <td key={col.key} className={`px-2 py-3 align-top font-semibold whitespace-nowrap overflow-hidden truncate ${isCancelled ? 'text-red-800' : 'text-maroon-950'}`}>
            {order.order_details.indent_number}
          </td>
        );
      case 'status':
        return (
          <td key={col.key} className="px-2 py-3 align-top whitespace-nowrap overflow-hidden">
            <span className={`px-2 py-1 text-xs rounded-md border ${getStatusColor(order.order_details.status)}`}>
              {order.order_details.status}
            </span>
          </td>
        );
      case 'date':
        return <td key={col.key} className={`px-2 py-3 align-top whitespace-nowrap overflow-hidden truncate ${textColor}`}>{formatDate(order.order_details.order_date)}</td>;
      case 'purchase_indent_number':
        return <td key={col.key} className={`px-2 py-3 align-top break-words ${textColor}`}>{renderMultiValueCell(order.suppliers.map((s) => s.purchase_indent_number || '-'))}</td>;
      case 'buyer':
        return (
          <td key={col.key} className={`px-2 py-3 align-top break-words ${textColor}`}>
            {order.buyers.length > 0 ? order.buyers.map((b, idx) => <div key={idx} className="leading-snug break-words">{b.buyer_name}</div>) : 'N/A'}
          </td>
        );
      case 'supplier':
        return (
          <td key={col.key} className={`px-2 py-3 align-top break-words ${textColor}`}>
            {order.suppliers.length > 0 ? order.suppliers.map((s, idx) => <div key={idx} className="leading-snug break-words">{s.supplier_name}</div>) : 'N/A'}
          </td>
        );
      case 'purchase_rate':
        return <td key={col.key} className={`px-2 py-3 align-top text-right break-words ${textColor}`}>{renderMultiValueCell(fabricLines.map((f) => `₹${f.purchase_rate}`))}</td>;
      case 'purchase_cd_percent':
        return <td key={col.key} className={`px-2 py-3 align-top text-right break-words ${textColor}`}>{renderMultiValueCell(fabricLines.map((f) => (f.purchase_cd_percent ? `${f.purchase_cd_percent}%` : '-')))}</td>;
      case 'sales_rate':
        return <td key={col.key} className={`px-2 py-3 align-top text-right break-words ${textColor}`}>{renderMultiValueCell(fabricLines.map((f) => `₹${f.sales_rate}`))}</td>;
      case 'sales_cd_percent':
        return <td key={col.key} className={`px-2 py-3 align-top text-right break-words ${textColor}`}>{renderMultiValueCell(fabricLines.map((f) => (f.sales_cd_percent ? `${f.sales_cd_percent}%` : '-')))}</td>;
      case 'total_qty':
        return <td key={col.key} className={`px-2 py-3 align-top text-right whitespace-nowrap overflow-hidden truncate ${textColor}`}>{order.total_quantity.toFixed(2)}</td>;
      case 'delivered_qty':
        return <td key={col.key} className={`px-2 py-3 align-top text-right whitespace-nowrap overflow-hidden truncate ${textColor}`}>{getDeliveredQty(order).toFixed(2)}</td>;
      case 'remaining':
        return <td key={col.key} className={`px-2 py-3 align-top text-right whitespace-nowrap overflow-hidden truncate ${textColor}`}>{order.remaining_balance.toFixed(2)}</td>;
      case 'cancelled_qty':
        return <td key={col.key} className={`px-2 py-3 align-top text-right whitespace-nowrap overflow-hidden truncate ${textColor}`}>{getCancelledQty(order).toFixed(2)}</td>;
      case 'deliveries':
        return <td key={col.key} className={`px-2 py-3 align-top text-right whitespace-nowrap overflow-hidden truncate ${textColor}`}>{order.deliveries.length}</td>;
      case 'trade_name':
        return <td key={col.key} className={`px-2 py-3 align-top break-words ${textColor}`}>{renderMultiValueCell(fabricLines.map((f) => f.trade_name))}</td>;
      case 'fabric_type':
        return <td key={col.key} className={`px-2 py-3 align-top break-words ${textColor}`}>{renderMultiValueCell(fabricLines.map((f) => f.fabric_type))}</td>;
      case 'design_color_number':
        return <td key={col.key} className={`px-2 py-3 align-top break-words ${textColor}`}>{renderMultiValueCell(fabricLines.map((f) => (f.design_number !== '-' ? f.design_number : f.color_number)))}</td>;
      case 'transporter_name':
        return <td key={col.key} className={`px-2 py-3 align-top break-words ${textColor}`}>{renderMultiValueCell(dispatchLines.map((d) => d.transporter_name))}</td>;
      case 'lr_number':
        return <td key={col.key} className={`px-2 py-3 align-top break-words ${textColor}`}>{renderMultiValueCell(dispatchLines.map((d) => d.lr_number))}</td>;
      case 'bale_number':
        return <td key={col.key} className={`px-2 py-3 align-top break-words ${textColor}`}>{renderMultiValueCell(dispatchLines.map((d) => d.bale_number))}</td>;
      case 'delivery_date':
        return <td key={col.key} className={`px-2 py-3 align-top whitespace-nowrap overflow-hidden truncate ${textColor}`}>{renderMultiValueCell(dispatchLines.map((d) => formatDate(d.delivery_date)))}</td>;
      default:
        return <td key={col.key}>-</td>;
    }
  };


  useEffect(() => {
    fetchOrders();
    fetchMasterData();
  }, []);

  useScrollRestoration('orders', !loading);

  const fetchMasterData = async () => {
    try {
      const [buyersRes, suppliersRes, printedRes, dyedRes] = await Promise.all([
        getMasterData('buyer'),
        getMasterData('supplier'),
        getMasterData('trade_name', 'Printed'),
        getMasterData('trade_name', 'Dyed')
      ]);
      setMasterBuyers(buyersRes.data || []);
      setMasterSuppliers(suppliersRes.data || []);
      // Combine printed and dyed trade names
      const allTradeNames = [
        ...(printedRes.data || []).map(t => ({ ...t, type: 'Printed' })),
        ...(dyedRes.data || []).map(t => ({ ...t, type: 'Dyed' }))
      ];
      setMasterTradeNames(allTradeNames);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Filter out empty values before sending
      const cleanFilters = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) {
          cleanFilters[key] = value.trim();
        }
      });
      const response = await getOrders(cleanFilters);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchOrders();
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;

    try {
      await deleteOrder(orderToDelete.id);
      toast.success('Order moved to Recycle Bin');
      setOrders(orders.filter((o) => o.id !== orderToDelete.id));
    } catch (error) {
      console.error('Failed to delete order:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'Super Admin';

  const handleEnterSelectionMode = () => {
    setSelectionMode(true);
    setSelectedIds(new Set());
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const toggleRowSelection = (orderId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const allVisibleSelected = sortedOrders.length > 0 && sortedOrders.every((o) => selectedIds.has(o.id));

  const handleToggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedOrders.map((o) => o.id)));
    }
  };

  const handleBulkDeleteConfirm = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(ids.map((id) => deleteOrder(id)));
      toast.success(`${ids.length} order${ids.length > 1 ? 's' : ''} moved to Recycle Bin`);
      setOrders((prev) => prev.filter((o) => !selectedIds.has(o.id)));
      setSelectionMode(false);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete some orders');
    } finally {
      setBulkDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleInfoClick = (order) => {
    setSelectedOrderInfo(order);
    setInfoDialogOpen(true);
  };

  const formatTimestampIST = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Filter orders based on current filters
  const getFilteredOrders = () => {
    return orders.filter(order => {
      const matchesIndent = !filters.indent_number || 
        order.order_details?.indent_number?.toLowerCase().includes(filters.indent_number.toLowerCase());
      const matchesBuyer = !filters.buyer || 
        order.buyers?.some(b => b.buyer_name?.toLowerCase().includes(filters.buyer.toLowerCase()));
      const matchesSupplier = !filters.supplier || 
        order.suppliers?.some(s => s.supplier_name?.toLowerCase().includes(filters.supplier.toLowerCase()));
      const matchesStatus = !filters.status || filters.status === 'all' || 
        order.order_details?.status === filters.status;
      return matchesIndent && matchesBuyer && matchesSupplier && matchesStatus;
    });
  };

  const filteredOrders = getFilteredOrders();
  const hasActiveFilters = filters.indent_number || filters.buyer || filters.supplier || (filters.status && filters.status !== 'all');

  // Export handler
  const handleExport = async (format, scope) => {
    const dataToExport = scope === 'current' ? filteredOrders : orders;
    
    if (dataToExport.length === 0) {
      return { success: false, message: 'No data available to export.' };
    }

    const filterDesc = [];
    if (filters.indent_number) filterDesc.push(`Indent: ${filters.indent_number}`);
    if (filters.buyer) filterDesc.push(`Buyer: ${filters.buyer}`);
    if (filters.supplier) filterDesc.push(`Supplier: ${filters.supplier}`);
    if (filters.status && filters.status !== 'all') filterDesc.push(`Status: ${filters.status}`);

    if (format === 'excel') {
      return exportToExcel(dataToExport, EXPORT_COLUMNS.orders, 'Orders', 'Orders');
    } else {
      return exportToPDF(dataToExport, EXPORT_COLUMNS.orders, 'Orders', 'Orders Report', filterDesc);
    }
  };

  return (
    <div className="space-y-3 lg:space-y-4" data-testid="orders-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-maroon-950">Orders</h1>
        </div>
        <div className="flex gap-2 w-full sm:w-auto items-center flex-wrap">
          {isDesktopView && selectionMode ? (
            <>
              <span className="text-sm font-medium text-maroon-700" data-testid="selected-count">
                {selectedIds.size} selected
              </span>
              <Button
                data-testid="delete-selected-button"
                onClick={() => setBulkDeleteDialogOpen(true)}
                disabled={selectedIds.size === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedIds.size})
              </Button>
              <Button
                data-testid="cancel-selection-button"
                variant="outline"
                onClick={handleCancelSelection}
                className="border-maroon-300 text-maroon-700 hover:bg-maroon-50"
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              {isDesktopView && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      data-testid="columns-manager-button"
                      variant="outline"
                      className="border-2 border-maroon-300 text-maroon-700 hover:bg-maroon-50 flex-1 sm:flex-none"
                      title="Manage Columns"
                    >
                      <Columns3 className="h-4 w-4 mr-2 sm:mr-0" strokeWidth={2.5} />
                      <span className="sm:hidden">Columns</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80 max-h-[70vh] overflow-y-auto bg-white p-3">
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-maroon-900 mb-1.5">Manage Columns</p>
                      <p className="text-xs text-maroon-500 mb-1.5">Drag column headers in the table to reorder them.</p>
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          type="button"
                          data-testid="reset-order-button"
                          onClick={resetColumnOrder}
                          className="text-xs text-maroon-600 hover:text-maroon-900 underline"
                        >
                          Reset Order
                        </button>
                        <button
                          type="button"
                          data-testid="reset-widths-button"
                          onClick={resetColumnWidths}
                          className="text-xs text-maroon-600 hover:text-maroon-900 underline"
                        >
                          Reset Widths
                        </button>
                        <button
                          type="button"
                          data-testid="reset-columns-button"
                          onClick={resetColumns}
                          className="text-xs text-maroon-600 hover:text-maroon-900 underline"
                        >
                          Reset to Default
                        </button>
                      </div>
                    </div>
                    {COLUMN_GROUPS.map((group) => (
                      <div key={group} className="mb-3">
                        <p className="text-xs font-semibold text-maroon-500 uppercase tracking-wide mb-1.5">{group}</p>
                        <div className="space-y-1.5">
                          {COLUMN_DEFINITIONS.filter((c) => c.group === group).map((col) => (
                            <label key={col.key} className="flex items-center gap-2 text-sm text-maroon-800 cursor-pointer">
                              <input
                                type="checkbox"
                                data-testid={`column-toggle-${col.key}`}
                                checked={isColumnVisible(col.key)}
                                onChange={() => toggleColumn(col.key)}
                                className="h-4 w-4 cursor-pointer"
                              />
                              {col.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </PopoverContent>
                </Popover>
              )}
              <Button
                data-testid="export-button"
                variant="outline"
                onClick={() => setExportDialogOpen(true)}
                className="border-2 border-maroon-300 text-maroon-700 hover:bg-maroon-50 flex-1 sm:flex-none"
                title="Export (Alt+E)"
              >
                <Download className="h-4 w-4 mr-2 sm:mr-0" strokeWidth={2.5} />
                <span className="sm:hidden">Export</span>
              </Button>
              {isDesktopView && isAdmin && (
                <Button
                  data-testid="delete-orders-button"
                  variant="outline"
                  onClick={handleEnterSelectionMode}
                  className="border-2 border-red-300 text-red-600 hover:bg-red-50 flex-1 sm:flex-none"
                  title="Delete Orders"
                >
                  <Trash2 className="h-4 w-4 mr-2 sm:mr-0" strokeWidth={2.5} />
                  <span className="sm:hidden">Delete</span>
                </Button>
              )}
              <Button
                data-testid="create-order-button"
                onClick={() => navigate('/orders/create')}
                className="bg-maroon-900 hover:bg-maroon-800 transition-colors duration-200 flex-1 sm:flex-none"
                title="Create Order"
              >
                <Plus className="h-4 w-4 mr-2 sm:mr-0" strokeWidth={2.5} />
                <span className="sm:hidden">Create Order</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters - mobile only; desktop uses per-column filters in the table header */}
      {!isDesktopView && (
      <Card className="border-maroon-200">
        <CardContent className="p-4 lg:pt-5">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 lg:gap-3">
            <div>
              <Input
                data-testid="filter-indent-input"
                placeholder="Indent No."
                value={filters.indent_number}
                onChange={(e) => setFilters({ ...filters, indent_number: e.target.value })}
                className="h-9 lg:h-10 text-sm"
              />
            </div>
            <div>
              <MasterDataAutocomplete
                value={filters.buyer}
                onValueChange={(value) => setFilters({ ...filters, buyer: value })}
                options={[
                  { value: '', label: 'All Buyers', pinned: true },
                  ...masterBuyers.map((buyer) => ({ value: buyer.name, label: buyer.name })),
                ]}
                placeholder="Buyer"
                searchPlaceholder="Search buyers..."
                inputClassName="h-9 lg:h-10 text-sm"
                testId="filter-buyer-select"
              />
            </div>
            <div>
              <MasterDataAutocomplete
                value={filters.supplier}
                onValueChange={(value) => setFilters({ ...filters, supplier: value })}
                options={[
                  { value: '', label: 'All Suppliers', pinned: true },
                  ...masterSuppliers.map((supplier) => ({ value: supplier.name, label: supplier.name })),
                ]}
                placeholder="Supplier"
                searchPlaceholder="Search suppliers..."
                inputClassName="h-9 lg:h-10 text-sm"
                testId="filter-supplier-select"
              />
            </div>
            <div>
              <MasterDataAutocomplete
                value={filters.trade_name}
                onValueChange={(value) => setFilters({ ...filters, trade_name: value })}
                options={[
                  { value: '', label: 'All Trade Names', pinned: true },
                  ...masterTradeNames.map((trade) => ({ value: trade.name, label: trade.name, description: trade.type || '' })),
                ]}
                placeholder="Trade Name"
                searchPlaceholder="Search trade items..."
                inputClassName="h-9 lg:h-10 text-sm"
                testId="filter-trade-name-select"
              />
            </div>
            <div>
              <Select 
                value={filters.status || "ALL_STATUS"} 
                onValueChange={(value) => setFilters({ ...filters, status: value === "ALL_STATUS" ? "" : value })}
              >
                <SelectTrigger data-testid="filter-status-select" className="h-9 lg:h-10 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="ALL_STATUS">All Status</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="search-button"
                onClick={handleSearch}
                className="bg-maroon-900 hover:bg-maroon-800 h-9 lg:h-10 transition-colors duration-200 flex-1"
              >
                <Search className="h-4 w-4 mr-1" />
                Search
              </Button>
              {(filters.buyer || filters.supplier || filters.trade_name || filters.status || filters.indent_number) && (
                <Button
                  data-testid="clear-filters-button"
                  onClick={() => {
                    setFilters({ status: '', buyer: '', supplier: '', trade_name: '', indent_number: '' });
                    fetchOrders();
                  }}
                  variant="outline"
                  className="h-9 lg:h-10 px-2 border-maroon-300 text-maroon-700 hover:bg-maroon-50"
                  title="Clear filters"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Orders List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="text-maroon-600">Loading orders...</div>
        </div>
      ) : orders.length === 0 ? (
        <Card className="border-maroon-200">
          <CardContent className="py-12 lg:py-11">
            <p className="text-center text-maroon-600">No orders found. Create your first order!</p>
          </CardContent>
        </Card>
      ) : isDesktopView ? (
        /* Desktop: single-row table with a frozen header, page-level scroll only */
        <div className="border border-maroon-200 rounded-lg bg-white">
          <table className="w-full text-sm border-collapse rounded-lg" style={{ tableLayout: 'fixed' }}>
            <thead className="sticky top-0 z-10 bg-maroon-50 shadow-sm">
              <tr className="border-b border-maroon-200 divide-x divide-maroon-900/10">
                {selectionMode && (
                  <th
                    style={{ width: `${(CHECKBOX_COLUMN_WEIGHT / getTotalColumnWeight(getVisibleColumnsOrdered())) * 100}%` }}
                    className="px-2 py-3"
                  >
                    <input
                      type="checkbox"
                      data-testid="select-all-checkbox"
                      aria-label="Select all orders"
                      checked={allVisibleSelected}
                      onChange={handleToggleSelectAll}
                      className="h-4 w-4 cursor-pointer"
                    />
                  </th>
                )}
                {(() => {
                  const visibleCols = getVisibleColumnsOrdered();
                  const totalWeight = getTotalColumnWeight(visibleCols);
                  return visibleCols.map((col, idx) => renderColumnHeader(col, idx, visibleCols, totalWeight));
                })()}
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map((order) => {
                const isCancelled = isCancelledOrder(order.order_details.status);
                const visibleCols = getVisibleColumnsOrdered();
                const totalWeight = getTotalColumnWeight(visibleCols);
                const needsFabricLines = visibleCols.some((c) => ['purchase_rate', 'purchase_cd_percent', 'sales_rate', 'sales_cd_percent', 'trade_name', 'fabric_type', 'design_color_number'].includes(c.key));
                const needsDispatchLines = visibleCols.some((c) => ['transporter_name', 'lr_number', 'bale_number', 'delivery_date'].includes(c.key));
                const fabricLines = needsFabricLines ? getFabricLines(order) : [];
                const dispatchLines = needsDispatchLines ? getDispatchLines(order) : [];
                return (
                  <tr
                    key={order.id}
                    tabIndex={0}
                    role="button"
                    aria-label={selectionMode ? `Select order ${order.order_details.indent_number}` : `Open order ${order.order_details.indent_number}`}
                    data-testid={`order-row-${order.id}`}
                    onClick={() => (selectionMode ? toggleRowSelection(order.id) : navigate(`/orders/${order.id}`))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        selectionMode ? toggleRowSelection(order.id) : navigate(`/orders/${order.id}`);
                      }
                    }}
                    className={`cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-maroon-600 border-b last:border-b-0 divide-x divide-maroon-900/10 transition-colors duration-150 ${
                      selectedIds.has(order.id)
                        ? 'bg-maroon-100 hover:bg-maroon-100 border-maroon-200'
                        : isCancelled
                        ? 'bg-red-50 hover:bg-red-100 border-red-200'
                        : 'bg-white hover:bg-maroon-50/70 border-maroon-100'
                    }`}
                  >
                    {selectionMode && (
                      <td
                        style={{ width: `${(CHECKBOX_COLUMN_WEIGHT / totalWeight) * 100}%` }}
                        className="px-2 py-3 align-top"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          data-testid={`select-order-${order.id}`}
                          aria-label={`Select order ${order.order_details.indent_number}`}
                          checked={selectedIds.has(order.id)}
                          onChange={() => toggleRowSelection(order.id)}
                          className="h-4 w-4 cursor-pointer"
                        />
                      </td>
                    )}
                    {visibleCols.map((col) => renderCell(col, order, fabricLines, dispatchLines, isCancelled))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 lg:gap-3">
          {orders.map((order) => {
            const isCancelled = isCancelledOrder(order.order_details.status);
            return (
              <Card
                key={order.id}
                tabIndex={0}
                role="button"
                aria-label={`Open order ${order.order_details.indent_number}`}
                className={`hover:shadow-md transition-all duration-300 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-maroon-600 focus-visible:ring-offset-2 ${isCancelled ? 'bg-red-100 border-red-400 border-2' : 'border-maroon-200 hover:border-maroon-300'}`}
                data-testid={`order-card-${order.id}`}
                onClick={() => navigate(`/orders/${order.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/orders/${order.id}`);
                  }
                }}
              >
                <CardContent className="p-3 lg:p-4">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between lg:justify-start gap-2 lg:gap-3 mb-1.5">
                        <h3 className={`text-lg lg:text-xl font-semibold ${isCancelled ? 'text-red-800' : 'text-maroon-950'}`}>
                          {order.order_details.indent_number}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs rounded-md border flex-shrink-0 ${getStatusColor(
                            order.order_details.status
                          )}`}
                        >
                          {order.order_details.status}
                        </span>
                      </div>
                      <div className={`grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-1 lg:gap-x-4 lg:gap-y-1.5 text-sm lg:text-base mt-1.5`}>
                        <div className="min-w-0">
                          <span className={`text-xs lg:text-sm ${isCancelled ? 'text-red-500' : 'text-maroon-500'}`}>Date</span>
                          <p className={`font-medium truncate leading-tight ${isCancelled ? 'text-red-800' : 'text-gray-900'}`}>
                            {formatDate(order.order_details.order_date)}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <span className={`text-xs lg:text-sm ${isCancelled ? 'text-red-500' : 'text-maroon-500'}`}>Buyers</span>
                          <p className={`font-medium truncate leading-tight ${isCancelled ? 'text-red-800' : 'text-gray-900'}`}>
                            {order.buyers.map((b) => b.buyer_name).join(', ') || 'N/A'}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <span className={`text-xs lg:text-sm ${isCancelled ? 'text-red-500' : 'text-maroon-500'}`}>Suppliers</span>
                          <p className={`font-medium truncate leading-tight ${isCancelled ? 'text-red-800' : 'text-gray-900'}`}>
                            {order.suppliers.map((s) => s.supplier_name).join(', ') || 'N/A'}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <span className={`text-xs lg:text-sm ${isCancelled ? 'text-red-500' : 'text-maroon-500'}`}>Total Qty</span>
                          <p className={`font-medium leading-tight ${isCancelled ? 'text-red-800' : 'text-gray-900'}`}>
                            {order.total_quantity.toFixed(2)}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <span className={`text-xs lg:text-sm ${isCancelled ? 'text-red-500' : 'text-maroon-500'}`}>Remaining</span>
                          <p className={`font-medium leading-tight ${isCancelled ? 'text-red-800' : 'text-gray-900'}`}>
                            {order.remaining_balance.toFixed(2)}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <span className={`text-xs lg:text-sm ${isCancelled ? 'text-red-500' : 'text-maroon-500'}`}>Deliveries</span>
                          <p className={`font-medium leading-tight ${isCancelled ? 'text-red-800' : 'text-gray-900'}`}>
                            {order.deliveries.length}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div 
                      className="flex gap-2 justify-end lg:ml-4 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 p-0 border-maroon-300 hover:bg-maroon-50"
                            data-testid={`order-menu-${order.id}`}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem onClick={() => handleInfoClick(order)} className="cursor-pointer">
                            <Info className="h-4 w-4 mr-2" />
                            Info
                          </DropdownMenuItem>
                          {(user?.role === 'admin' || user?.role === 'Super Admin') && (
                            <DropdownMenuItem
                              data-testid={`delete-order-${order.id}`}
                              onClick={() => handleDeleteClick(order)}
                              className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Info Dialog */}
      <Dialog open={infoDialogOpen} onOpenChange={setInfoDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>Order Information</DialogTitle>
          </DialogHeader>
          {selectedOrderInfo && (
            <div className="space-y-4 pt-4">
              <div className="border-b border-maroon-200 pb-3">
                <p className="text-xs font-semibold text-maroon-600 uppercase mb-1">Order Number</p>
                <p className="text-base font-semibold text-maroon-950">
                  {selectedOrderInfo.order_details.indent_number}
                </p>
              </div>
              <div className="border-b border-maroon-200 pb-3">
                <p className="text-xs font-semibold text-maroon-600 uppercase mb-1">Created On</p>
                <p className="text-sm text-maroon-950">{formatTimestampIST(selectedOrderInfo.created_at)}</p>
                <p className="text-xs text-maroon-600 mt-1">India Standard Time (IST)</p>
              </div>
              <div className="border-b border-maroon-200 pb-3">
                <p className="text-xs font-semibold text-maroon-600 uppercase mb-1">Last Updated</p>
                <p className="text-sm text-maroon-950">{formatTimestampIST(selectedOrderInfo.updated_at)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-maroon-600 uppercase mb-1">Created By</p>
                <p className="text-sm text-maroon-950">User ID: {selectedOrderInfo.created_by}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete order &quot;{orderToDelete?.order_details.indent_number}&quot;? It will be
              moved to the Recycle Bin and can be restored later by an admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Order{selectedIds.size > 1 ? 's' : ''}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.size} selected order{selectedIds.size > 1 ? 's' : ''}? They
              will be moved to the Recycle Bin and can be restored later by an admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-bulk-delete-button"
              onClick={handleBulkDeleteConfirm}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {bulkDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onExport={handleExport}
        title="Export Orders"
        hasFilters={hasActiveFilters}
        filterDescription={[
          filters.indent_number && `Indent: ${filters.indent_number}`,
          filters.buyer && `Buyer: ${filters.buyer}`,
          filters.supplier && `Supplier: ${filters.supplier}`,
          filters.status && filters.status !== 'all' && `Status: ${filters.status}`,
        ].filter(Boolean).join(', ')}
        totalRecords={orders.length}
        filteredRecords={filteredOrders.length}
      />
    </div>
  );
}
